// Per-country lead rules (brief 2026-10-05): phone numbering and postcode shape.
// Pure.
//
// ⚠️ AU AND NZ TODAY, PER-LOCALE FROM THE START. The UK (+44) would follow, and UK postcodes are ALPHANUMERIC — so nothing here may assume
// "a postcode is four digits" or "the calling code is 61" outside this table.
// Adding a country is an entry, not a code change; "Australia only" must not
// quietly become "AU shapes hardcoded".
//
// ⚠️ AF PERFORMS NO NORMALISATION (it echoed +61400000000 verbatim), so this
// normaliser is the only thing between a badly formatted Meta phone number
// and a bad record. It REFUSES what it cannot read rather than guessing.

export interface LeadLocaleRules {
  /** The country as a person names it. */
  countryName: string;
  /** The postcode rule in words, for the route form. */
  postcodeDescription: string;
  /** ITU calling code, digits only. */
  callingCode: string;
  /** The domestic trunk prefix dropped when the calling code is added. */
  trunkPrefix: string;
  /** A valid NATIONAL significant number (no trunk prefix, no calling code). */
  nationalNumber: RegExp;
  /** A valid postcode, as the lead would type it after trimming. */
  postcode: RegExp;
}

export const LEAD_LOCALES = {
  // AU: mobiles 4xxxxxxxx; geographic 2/3/7/8 + 8 digits. 9 digits after the 0.
  AU: {
    countryName: 'Australia',
    postcodeDescription: '4 digits',
    callingCode: '61',
    trunkPrefix: '0',
    nationalNumber: /^[23478]\d{8}$/,
    postcode: /^\d{4}$/,
  },
  // NZ: national numbering plan (Commerce Commission's Telecommunications
  // Numbering Plan; Wikipedia "Telephone numbers in New Zealand" for the
  // summary). Mobiles are 02x (020, 021, 022, 025, 027, 028, 029): after the 0,
  // a 2 then 7-9 digits (8-10 in all). Geographic numbers use area codes
  // 3, 4, 6, 7 and 9 (the old 03/04/06/07/09) followed by 7 digits (8 in all).
  // 0800/0508/0900 non-geographic numbers are deliberately NOT accepted: a lead
  // reachable only on a freephone number cannot be called or texted back.
  NZ: {
    countryName: 'New Zealand',
    postcodeDescription: '4 digits',
    callingCode: '64',
    trunkPrefix: '0',
    nationalNumber: /^(?:2\d{7,9}|[34679]\d{7})$/,
    postcode: /^\d{4}$/,
  },
} as const satisfies Record<string, LeadLocaleRules>;

export type LeadLocale = keyof typeof LEAD_LOCALES;

export function isLeadLocale(value: unknown): value is LeadLocale {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(LEAD_LOCALES, value);
}

/** Generic E.164: '+', a non-zero digit, 7-15 digits in all. */
const E164 = /^\+[1-9]\d{6,14}$/;

/**
 * A phone number in E.164 for the given locale, or null when it cannot be read
 * as one. Accepts the shapes a Meta lead actually carries:
 *   0412 345 678 · (04) 1234 5678 · 61412345678 · +61412345678 · +61 0412 345 678
 * and passes an already-valid foreign E.164 number through unchanged (a NZ
 * visitor enquiring at an AU club is still reachable). A local-looking number
 * that does not fit the locale's numbering plan is REFUSED, never padded.
 */
export function toE164(raw: string | undefined, locale: LeadLocale): string | null {
  const rules: LeadLocaleRules = LEAD_LOCALES[locale];
  const trimmed = (raw ?? '').trim();
  if (trimmed === '') return null;
  // Only spacing and punctuation may be removed; any letter means it is not a number.
  if (!/^\+?[\d\s().-]+$/.test(trimmed)) return null;
  const plus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');

  let national: string | null = null;
  if (plus || digits.startsWith(rules.callingCode)) {
    if (digits.startsWith(rules.callingCode)) {
      national = digits.slice(rules.callingCode.length);
      // "+61 0412…" — the trunk prefix left in after the calling code.
      if (national.startsWith(rules.trunkPrefix) && !rules.nationalNumber.test(national)) {
        national = national.slice(rules.trunkPrefix.length);
      }
    } else {
      // Another country's E.164 number: valid as written, or not at all.
      const foreign = `+${digits}`;
      return E164.test(foreign) ? foreign : null;
    }
  } else if (digits.startsWith(rules.trunkPrefix)) {
    national = digits.slice(rules.trunkPrefix.length);
  }
  if (national === null || !rules.nationalNumber.test(national)) return null;
  return `+${rules.callingCode}${national}`;
}

/** Whether a (trimmed) postcode is valid for the locale. */
export function isValidPostcode(postcode: string, locale: LeadLocale): boolean {
  return LEAD_LOCALES[locale].postcode.test(postcode.trim());
}
